/**
 * Visit List Page - Modern Design
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, Eye, Edit, Calendar, ChevronLeft, ChevronRight, Wand2 } from 'lucide-react'
import { useVisits } from '@/hooks/useVisits'
import { useAuth } from '@/contexts/AuthContext'
import { officesApi, legalAssistanceTypesApi } from '@/api'
import type { Office, LegalAssistanceType } from '@/types/user'

export function VisitList() {
  const { user } = useAuth()

  // State for filters and pagination
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [legalAssistanceType, setLegalAssistanceType] = useState('')
  const [officeId, setOfficeId] = useState('')

  // State for filter options
  const [legalAssistanceTypes, setLegalAssistanceTypes] = useState<LegalAssistanceType[]>([])
  const [offices, setOffices] = useState<Office[]>([])
  const [loadingFilters, setLoadingFilters] = useState(true)

  // Fetch visits with filters
  const { data, isLoading, error, refetch } = useVisits(
    {
      search,
      legal_assistance_type_id: legalAssistanceType ? Number(legalAssistanceType) : undefined,
      office_id: officeId ? Number(officeId) : undefined
    },
    { page, limit }
  )

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [typesData, officesData] = await Promise.all([
          legalAssistanceTypesApi.getAll(),
          officesApi.getAll(),
        ])
        setLegalAssistanceTypes(typesData)
        setOffices(officesData)
      } catch (error) {
        console.error('Failed to load filters:', error)
      } finally {
        setLoadingFilters(false)
      }
    }

    fetchFilters()
  }, [])

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchInput.trim()) {
      setSearch(searchInput.trim())
      setPage(1)
    }
  }

  // Format client name from client_info JSONB
  const getClientName = (visit: any) => {
    const clientInfo = visit.client_info || {}
    const firstname = clientInfo.firstname || ''
    const lastname = clientInfo.lastname || ''
    return `${firstname} ${lastname}`.trim() || 'N/A'
  }

  // Get lawyer name with Atty. prefix
  const getLawyerName = (visit: any) => {
    if (visit.lawyer_name && visit.lawyer_name.trim()) {
      return `Atty. ${visit.lawyer_name}`
    }
    return 'Unassigned'
  }

  // Get legal assistance type
  const getLegalAssistance = (visit: any) => {
    return visit.assistance_type_description || 'N/A'
  }

  // Get action taken status with badge styling
  const getActionStatus = (visit: any) => {
    // Status 101 = Denied (from legacy system)
    if (visit.status === 101) {
      return { label: 'Denied', className: 'bg-red-100 text-red-800' }
    }
    // Any other status = Assisted
    if (visit.status !== null && visit.status !== undefined) {
      return { label: 'Assisted', className: 'bg-green-100 text-green-800' }
    }
    // No status
    return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' }
  }

  // Check if current user can edit
  const canEdit = (visit: any) => {
    return user && visit.user_id === user.id
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Visits</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Calendar className="h-8 w-8" />
            Visits
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and track client visits
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/visits/wizard">
            <button className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm font-medium">
              <Wand2 className="w-5 h-5" />
              Visit Wizard
            </button>
          </Link>
        </div>
      </div>

      {/* Search and Filters Card */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search Bar - Full Width */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by client name or control number..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
            <button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('')
                  setSearchInput('')
                  setPage(1)
                }}
                className="border border-gray-300 hover:bg-gray-50 px-6 py-3 rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Legal Assistance Filter */}
            <div className="relative flex-1">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
              <select
                value={legalAssistanceType}
                onChange={(e) => {
                  setLegalAssistanceType(e.target.value)
                  setPage(1)
                }}
                disabled={loadingFilters}
                className="w-full appearance-none pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">All Assistance Types</option>
                {legalAssistanceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.description || type.type}
                  </option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 rotate-90 pointer-events-none" />
            </div>

            {/* Office Filter */}
            <div className="relative flex-1">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
              <select
                value={officeId}
                onChange={(e) => {
                  setOfficeId(e.target.value)
                  setPage(1)
                }}
                disabled={loadingFilters}
                className="w-full appearance-none pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">All Offices</option>
                {offices.map((office) => (
                  <option key={office.id} value={office.id}>
                    {office.office}
                  </option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 rotate-90 pointer-events-none" />
            </div>
          </div>
        </form>

        {/* Active Filters */}
        {search && (
          <div className="mt-4 pt-4 border-t flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Active filters:</span>
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
              Search: "{search}"
            </span>
          </div>
        )}
      </div>

      {/* Results Summary */}
      {data && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{data.items.length}</span> of{' '}
            <span className="font-semibold text-gray-900">{data.total}</span> visits
          </p>
          {data.pages > 1 && (
            <p className="text-gray-600">
              Page <span className="font-semibold text-gray-900">{page}</span> of{' '}
              <span className="font-semibold text-gray-900">{data.pages}</span>
            </p>
          )}
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-gray-600">Loading visits...</p>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No visits found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or create a new visit</p>
            <Link to="/visits/wizard">
              <button className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg transition-colors">
                Create First Visit
              </button>
            </Link>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Control No.
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Client Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Legal Assistance
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Lawyer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.items.map((visit) => {
                    const clientName = getClientName(visit)
                    const lawyerName = getLawyerName(visit)
                    const legalAssistance = getLegalAssistance(visit)
                    const statusInfo = getActionStatus(visit)
                    const userCanEdit = canEdit(visit)

                    return (
                      <tr
                        key={visit.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => window.location.href = `/visits/${visit.id}`}
                      >
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {visit.control_number || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">
                            {clientName}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-700">
                          {legalAssistance}
                        </td>
                        <td className="px-6 py-4 text-gray-700">
                          {lawyerName}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            {/* View Button */}
                            <Link to={`/visits/${visit.id}`}>
                              <button
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium transition-colors"
                                title="View Visit"
                              >
                                <Eye className="w-4 h-4" />
                                <span className="text-sm">View</span>
                              </button>
                            </Link>

                            {/* Edit Button - Only if user is creator */}
                            {userCanEdit && (
                              <Link to={`/visits/${visit.id}/edit`}>
                                <button
                                  className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors"
                                  title="Edit Visit"
                                >
                                  <Edit className="w-4 h-4" />
                                  <span className="text-sm">Edit</span>
                                </button>
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.pages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {page} of {data.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                    className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(data.pages, p + 1))}
                    disabled={page === data.pages || isLoading}
                    className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default VisitList
